"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE_PATH = "/image/new-version/machining/five-axis/materials";

const metalMaterials = [
	{ key: "Aluminum", image: "aluminum.webp" },
	{ key: "Copper", image: "copper.webp" },
	{ key: "Brass", image: "brass.webp" },
	{ key: "Bronze", image: "bronze.webp" },
	{ key: "Steel", image: "steel.webp" },
	{ key: "StainlessSteel", image: "stainless-steel.webp" },
	{ key: "Magnesium", image: "magnesium.webp" },
	{ key: "Titanium", image: "titanium.webp" },
];

const plasticMaterials = [
	{ key: "ABS", image: "abs.webp" },
	{ key: "PC", image: "pc.webp" },
	{ key: "PMMA", image: "pmmaacrylic.webp" },
	{ key: "POM", image: "pom.webp" },
	{ key: "PA", image: "nylonpa.webp" },
	{ key: "PE", image: "pe.webp" },
	{ key: "PEEK", image: "peek.webp" },
	{ key: "PP", image: "pp.webp" },
	{ key: "HDPE", image: "hdpe.webp" },
	{ key: "HIPS", image: "hips.webp" },
	{ key: "LDPE", image: "ldpe.webp" },
	{ key: "PBT", image: "pbt.webp" },
	{ key: "PPA", image: "ppa.webp" },
	{ key: "PAI", image: "pai.webp" },
	{ key: "PET", image: "pet.webp" },
	{ key: "PPS", image: "pps.webp" },
	{ key: "PS", image: "ps.webp" },
	{ key: "PVC", image: "pvc.webp" },
	{ key: "PTFE", image: "ptfe.webp" },
	{ key: "UPE", image: "upe.webp" },
];

interface MaterialCardProps {
	materials: { key: string; image: string }[];
	type: "metal" | "plastic";
	title: string;
}

const MaterialCard = ({ materials, type, title }: MaterialCardProps) => {
	const t = useTranslations("fiveAxisMaterials");
	const [activeIndex, setActiveIndex] = useState(0);

	const activeMaterial = materials[activeIndex];
	const prefix = type === "metal" ? "metal" : "plastic";

	const materialName = t(`${prefix}${activeMaterial.key}` as any);
	const materialDesc = t(`${prefix}${activeMaterial.key}Desc` as any);
	const materialVariants = t(`${prefix}${activeMaterial.key}Variants` as any);

	return (
		<div className="flex flex-col">
			<h3 className="mb-6 ml-6 text-lg font-bold text-black sm:text-xl md:text-2xl">{title}</h3>
			<div className="flex flex-col gap-4 md:flex-row md:gap-0">
				<div className="max-h-[650px] overflow-y-auto bg-[#f1f1f1] md:w-44">
					<div className="flex flex-row flex-wrap gap-1 md:flex-col md:gap-0">
						{materials.map((material, index) => (
							<button
								key={material.key}
								onClick={() => setActiveIndex(index)}
								className={`px-4 py-3 text-center text-sm font-medium transition-colors ${
									index === activeIndex
										? "bg-[#ededed] text-themePrimary400"
										: "text-gray-900 hover:text-themePrimary400"
								}`}
							>
								{t(`${prefix}${material.key}` as any)}
							</button>
						))}
					</div>
				</div>

				<div className="flex flex-1 flex-col gap-3 bg-white p-4">
					<div className="relative aspect-[510/260] w-full overflow-hidden bg-white">
						<Image
							src={`${IMAGE_BASE_PATH}/${activeMaterial.image}`}
							alt={materialName}
							fill
							className="object-contain"
							sizes="(max-width: 768px) 100vw, 500px"
						/>
					</div>

					<div className="flex-1 bg-[#000000] p-5 text-white">
						<p className="mb-4 text-sm leading-relaxed opacity-70">{materialDesc}</p>
						<div>
							{type === "metal" && <p className="mb-2 text-sm opacity-70">{t("typicalBrand")}</p>}
							<div className="space-y-1">
								{materialVariants.split(",").map((variant: string, idx: number) => (
									<p key={idx} className="text-sm opacity-70">
										{variant.trim()}
									</p>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const AxisCncMachiningMaterials = () => {
	const t = useTranslations("fiveAxisMaterials");
	const tMilling = useTranslations("cncMillingBanner");

	return (
		<section className="w-full bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{tMilling("materialsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{tMilling("materialsSubtitle")}</p>
				</div>

				<div className="mx-auto grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
					<MaterialCard materials={metalMaterials} type="metal" title={t("metalsTitle")} />
					<MaterialCard materials={plasticMaterials} type="plastic" title={t("plasticsTitle")} />
				</div>
			</div>
		</section>
	);
};

export default AxisCncMachiningMaterials;
